import { useEffect, useMemo, useState } from 'react';
import ConfirmBar from './ConfirmBar';
import QuoteShareActions from './QuoteShareActions';
import { useT } from '../context/LocaleContext';
import { defaultBusinessProfile, fetchBusinessProfile } from '../lib/businessProfile';
import { money } from '../lib/format';
import { buildQuotePdf, quoteFileName } from '../lib/quotePdf';
import { buildLinesFromResaleInspection, buildLinesFromResaleRepairs, buildLinesFromWorkLog, mergeQuoteDraftWithResale, mergeQuoteDraftWithWorkLog } from '../lib/quoteFromWorkLog';
import { supabase } from '../lib/supabase';

const emptyLine = () => ({
  key: crypto.randomUUID(),
  description: '',
  quantity: 1,
  unit_price: '',
  job_card_part_id: null,
  linked_part_id: null,
  line_kind: 'custom',
  source: 'custom',
});

function qtyOf(value) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n < 1) return 1;
  return n;
}

function lineTotal(line) {
  const unit = Number(line.unit_price);
  if (Number.isNaN(unit)) return 0;
  return qtyOf(line.quantity) * unit;
}

function linesFromQuote(quote) {
  const items = quote?.items || [];
  if (items.length === 0) return [emptyLine()];
  return items.map((item) => ({
    key: item.id,
    description: item.description || '',
    quantity: qtyOf(item.quantity),
    unit_price: item.unit_price == null ? '' : String(item.unit_price),
    job_card_part_id: item.job_card_part_id || null,
    linked_part_id: null,
    line_kind: item.line_kind || (item.job_card_part_id ? 'part' : 'custom'),
    source: item.job_card_part_id ? 'work_log' : 'custom',
  }));
}

function canRevise(status) {
  return !status || ['sent', 'rejected', 'partial', 'draft'].includes(status);
}

function normalizeQuoteRow(row) {
  if (!row || typeof row !== 'object') return null;
  const items = row.items || row.quotation_items || [];
  return {
    ...row,
    items: [...items].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
  };
}

/** Prefer the RPC used by the customer app — nested RLS on items is unreliable. */
async function fetchBookingQuote(bookingId, quoteKind) {
  const { data: rpcData, error: rpcError } = await supabase.rpc(
    'get_booking_quotations',
    { p_booking_id: bookingId },
  );
  if (!rpcError && Array.isArray(rpcData)) {
    const rows = rpcData
      .map((row) => (row?.payload && typeof row.payload === 'object' ? row.payload : row))
      .filter((row) => row && typeof row === 'object' && row.id);
    const match =
      rows.find((row) => (row.quote_kind || 'repair') === quoteKind) ||
      (quoteKind === 'repair'
        ? rows.find((row) => !row.quote_kind || row.quote_kind === 'repair')
        : null);
    if (match) return { data: normalizeQuoteRow(match), error: null };
    if (rows.length === 0) return { data: null, error: null };
  }

  const { data, error } = await supabase
    .from('quotations')
    .select('*, items:quotation_items(*)')
    .eq('booking_id', bookingId)
    .eq('quote_kind', quoteKind)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return { data: null, error };
  return { data: normalizeQuoteRow(data), error: null };
}

export default function QuotePanel({
  booking,
  workLogVersion = 0,
  variant = 'standard',
  quoteKind = 'repair',
  resaleReport = null,
  onSent,
}) {
  const t = useT();
  const isResale = variant === 'resale';
  const isResaleInspection = isResale && quoteKind === 'inspection';
  const isResaleRepair = isResale && quoteKind === 'repair';
  const [quote, setQuote] = useState(null);
  const [draft, setDraft] = useState([emptyLine()]);
  const [note, setNote] = useState('');
  const [workParts, setWorkParts] = useState([]);
  const [healthReport, setHealthReport] = useState(null);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(defaultBusinessProfile);
  const [editing, setEditing] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const visitClosed = booking.status === 'cancelled' || booking.status === 'completed';
  const revisable = !visitClosed && canRevise(quote?.status);
  const showForm = revisable && (!quote || editing);

  async function loadWorkContext() {
    const { data: cardId, error: cardError } = await supabase.rpc(
      'ensure_job_card_for_booking_id',
      { p_booking_id: booking.id },
    );
    if (cardError) {
      return { parts: [], health: null };
    }
    const [{ data: parts }, { data: health }] = await Promise.all([
      supabase
        .from('job_card_parts')
        .select('*')
        .eq('job_card_id', cardId)
        .order('created_at', { ascending: true }),
      supabase
        .from('vehicle_health_reports')
        .select(
          'diagnostic_findings, maintenance_history_notes, accident_repair_notes',
        )
        .eq('booking_id', booking.id)
        .eq('status', 'finalized')
        .maybeSingle(),
    ]);
    return { parts: parts || [], health: health || null };
  }

  function applyInspectionToDraft(baseDraft, servicePrice) {
    const lines = buildLinesFromResaleInspection(booking, resaleReport, servicePrice);
    return mergeQuoteDraftWithResale(baseDraft, lines);
  }

  function applyResaleToDraft(baseDraft) {
    const resaleLines = buildLinesFromResaleRepairs(resaleReport?.immediate_repairs);
    if (resaleLines.length === 0) return baseDraft;
    return mergeQuoteDraftWithResale(baseDraft, resaleLines);
  }

  function applyWorkLogToDraft(baseDraft, parts, health) {
    const workLines = buildLinesFromWorkLog(parts, booking, health);
    return mergeQuoteDraftWithWorkLog(baseDraft, workLines);
  }

  function applySeedToDraft(baseDraft, parts, health, servicePrice) {
    if (isResaleInspection) return applyInspectionToDraft(baseDraft, servicePrice);
    if (isResaleRepair) return applyResaleToDraft(baseDraft);
    return applyWorkLogToDraft(baseDraft, parts, health);
  }

  async function loadInspectionServicePrice() {
    const name = booking?.service_type || 'Pre-purchase inspection';
    const { data: exact } = await supabase
      .from('services')
      .select('price')
      .ilike('name', name)
      .eq('is_active', true)
      .maybeSingle();
    if (exact?.price != null) return exact.price;
    const { data: fallback } = await supabase
      .from('services')
      .select('price')
      .ilike('name', '%pre-purchase%')
      .eq('is_active', true)
      .maybeSingle();
    return fallback?.price ?? null;
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const servicePricePromise = isResaleInspection
        ? loadInspectionServicePrice()
        : Promise.resolve(null);
      const workPromise = isResale
        ? Promise.resolve({ parts: [], health: null })
        : loadWorkContext();
      const quotePromise = fetchBookingQuote(booking.id, quoteKind);

      const [servicePrice, { parts, health }, { data, error: queryError }] =
        await Promise.all([servicePricePromise, workPromise, quotePromise]);

      setWorkParts(parts);
      setHealthReport(health);

      if (queryError) {
        setError(queryError.message);
        setQuote(null);
        return;
      }
      if (!data) {
        setQuote(null);
        if (!visitClosed) {
          setDraft(applySeedToDraft([emptyLine()], parts, health, servicePrice));
          setNote(
            isResaleInspection
              ? `Pre-sale inspection quotation${resaleReport?.report_number ? ` for ${resaleReport.report_number}` : ''}.`
              : isResaleRepair && resaleReport?.summary_notes
                ? `Following certified pre-purchase inspection ${resaleReport.report_number || ''}.`.trim()
                : '',
          );
        }
        setEditing(false);
        return;
      }
      setQuote(data);
      setDraft(
        visitClosed
          ? linesFromQuote(data)
          : applySeedToDraft(linesFromQuote(data), parts, health, servicePrice),
      );
      setNote(data.note || '');
      setEditing(false);
    } catch (loadError) {
      setError(loadError?.message || 'Could not load quotation.');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }

  function syncFromResaleRepairs() {
    const resaleLines = buildLinesFromResaleRepairs(resaleReport?.immediate_repairs);
    setDraft((current) => mergeQuoteDraftWithResale(current, resaleLines));
    setOk('Pulled in the latest repair lines from the inspection.');
  }

  function syncFromInspectionFee() {
    loadInspectionServicePrice().then((servicePrice) => {
      const lines = buildLinesFromResaleInspection(booking, resaleReport, servicePrice);
      setDraft((current) => mergeQuoteDraftWithResale(current, lines));
      setOk('Refreshed the inspection service line from the catalog.');
    });
  }

  function syncFromWorkLog() {
    const workLines = buildLinesFromWorkLog(workParts, booking, healthReport);
    setDraft((current) => mergeQuoteDraftWithWorkLog(current, workLines));
    setOk('Pulled in the latest work log and inspection lines.');
  }

  useEffect(() => {
    load();
  }, [booking.id, isResale, isResaleInspection, isResaleRepair, quoteKind, resaleReport?.id]);

  useEffect(() => {
    if (!workLogVersion || isResale) return;
    loadWorkContext().then(({ parts, health }) => {
      setWorkParts(parts);
      setHealthReport(health);
      if (!revisable) return;
      setDraft((current) => applyWorkLogToDraft(current, parts, health));
    });
  }, [workLogVersion]);

  useEffect(() => {
    let alive = true;
    fetchBusinessProfile()
      .then((next) => {
        if (alive) setBusiness(next);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const draftTotal = useMemo(
    () => draft.reduce((sum, line) => sum + lineTotal(line), 0),
    [draft]
  );

  function setLine(key, patch) {
    setDraft((current) =>
      current.map((line) => {
        if (line.key !== key) return line;
        const next = { ...line, ...patch };
        if (patch.quantity !== undefined) next.quantity = qtyOf(patch.quantity);
        return next;
      })
    );
  }

  async function send(event) {
    event.preventDefault();
    const items = draft
      .map((line) => ({
        description: line.description.trim(),
        quantity: qtyOf(line.quantity),
        unit_price: Number(line.unit_price),
        job_card_part_id: line.job_card_part_id || null,
        line_kind: line.line_kind || 'custom',
      }))
      .filter((line) => line.description);
    if (items.length === 0) {
      setError('Add at least one charge.');
      return;
    }
    if (items.some((line) => line.quantity < 1 || line.unit_price < 0 || Number.isNaN(line.unit_price))) {
      setError('Every line needs a quantity of at least 1 and a price.');
      return;
    }
    const revising = Boolean(quote);
    if (!confirmSend) {
      setConfirmSend(true);
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    const { error: rpcError } = await supabase.rpc('send_booking_quotation', {
      p_booking_id: booking.id,
      p_items: items,
      p_note: note.trim() || null,
      p_quote_kind: quoteKind,
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setOk(
      revising
        ? 'Updated. The customer needs to approve it again.'
        : isResale
          ? 'Sent. The seller will see it in the app on this booking.'
          : 'Sent. The customer will see it in the app.',
    );
    setConfirmSend(false);
    load();
    onSent?.();
  }

  const sendLabel = !quote
    ? isResale
      ? t('quote.sendSeller')
      : t('quote.sendCustomer')
    : quote.status === 'sent'
      ? t('quote.update')
      : t('quote.sendRevised');

  const panelTitle = isResaleInspection
    ? t('quote.titleInspection')
    : isResaleRepair
      ? t('quote.titleRepair')
      : t('quote.title');
  const panelDescription = loading
    ? t('common.loading')
    : visitClosed
      ? quote
        ? t('quote.ledeClosed')
        : t('quote.ledeClosedEmpty')
      : isResaleInspection
        ? t('quote.ledeInspection')
        : isResaleRepair
          ? t('quote.ledeRepair')
          : t('quote.lede');

  const resaleRepairCount = buildLinesFromResaleRepairs(resaleReport?.immediate_repairs).length;

  async function exportPdf() {
    if (!quote) return;
    setExporting(true);
    setError('');
    setOk('');
    try {
      const doc = await buildQuotePdf(quote, booking, business, {
        quoteKind,
        title: panelTitle,
      });
      doc.save(quoteFileName(booking.id, quoteKind));
      setOk('PDF ready. Use your browser download to open it.');
    } catch (pdfError) {
      setError(pdfError?.message || 'Could not export PDF.');
    } finally {
      setExporting(false);
    }
  }

  async function shareQuote() {
    if (!quote) return;
    setExporting(true);
    setError('');
    setOk('');
    try {
      const doc = await buildQuotePdf(quote, booking, business, {
        quoteKind,
        title: panelTitle,
      });
      const blob = doc.output('blob');
      const file = new File([blob], quoteFileName(booking.id, quoteKind), {
        type: 'application/pdf',
      });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `Quotation ${booking.id}`,
          text: `Quotation for ${booking.service_type || 'visit'}`,
          files: [file],
        });
        setOk(t('quote.shared'));
        return;
      }

      doc.save(quoteFileName(booking.id, quoteKind));
      setOk(t('quote.downloaded'));
    } catch (shareError) {
      if (shareError?.name !== 'AbortError') {
        setError(shareError?.message || 'Could not share quotation.');
      }
    } finally {
      setExporting(false);
    }
  }

  const importedCount = draft.filter(
    (line) =>
      line.job_card_part_id ||
      line.linked_part_id ||
      line.source === 'work_log' ||
      line.source === 'inspection' ||
      line.source === 'resale_inspection' ||
      line.source === 'resale_inspection_fee',
  ).length;

  return (
    <article className="quote-sheet">
      <header className="quote-head">
        <span className="material-symbol">receipt_long</span>
        <div>
          <h2>{panelTitle}</h2>
          <p>{panelDescription}</p>
        </div>
      </header>
      {error && <p className="form-error">{error}</p>}
      {ok && <p className="form-ok">{ok}</p>}

      {loading ? (
        <p className="empty">{t('common.loading')}</p>
      ) : quote && !editing ? (
        <ol className="quote-history">
          <QuoteCard
            quote={quote}
            busy={exporting}
            onExport={exportPdf}
            onShare={shareQuote}
            onShareRefresh={(next) => setQuote((prev) => ({ ...prev, ...next }))}
            onEdit={
              revisable
                ? () => {
                    setDraft(applySeedToDraft(linesFromQuote(quote), workParts, healthReport));
                    setNote(quote.note || '');
                    setEditing(true);
                    setOk('');
                  }
                : null
            }
          />
        </ol>
      ) : !showForm && !visitClosed ? (
        <p className="empty">{t('quote.emptyOpen')}</p>
      ) : null}

      {showForm ? (
        <form className="quote-compose" onSubmit={send}>
          {quote ? (
            <div className="quote-edit-bar">
              <p className="quote-lede">
                {quote.status === 'sent'
                  ? t('quote.replace')
                  : t('quote.revise')}
              </p>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setEditing(false);
                  setConfirmSend(false);
                  setDraft(applySeedToDraft(linesFromQuote(quote), workParts, healthReport));
                  setNote(quote.note || '');
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : null}
          {showForm && isResaleInspection ? (
            <div className="quote-sync-banner">
              <p>{t('quote.catalogLine')}</p>
              <button type="button" className="ghost" onClick={syncFromInspectionFee}>
                {t('quote.refreshCatalog')}
              </button>
            </div>
          ) : null}
          {showForm && isResaleRepair && resaleRepairCount > 0 ? (
            <div className="quote-sync-banner">
              <p>
                {importedCount > 0
                  ? `${importedCount} line${importedCount === 1 ? '' : 's'} from inspection repairs.`
                  : `${resaleRepairCount} repair line${resaleRepairCount === 1 ? '' : 's'} ready to import.`}
              </p>
              <button type="button" className="ghost" onClick={syncFromResaleRepairs}>
                {t('quote.importInspection')}
              </button>
            </div>
          ) : null}
          {showForm && !isResale && workParts.length > 0 ? (
            <div className="quote-sync-banner">
              <p>
                {importedCount > 0
                  ? `${importedCount} line${importedCount === 1 ? '' : 's'} from work log / inspection.`
                  : `${workParts.length} work-log ${workParts.length === 1 ? 'entry' : 'entries'} ready to import.`}
              </p>
              <button type="button" className="ghost" onClick={syncFromWorkLog}>
                {t('quote.syncWorkLog')}
              </button>
            </div>
          ) : null}
          <div className="quote-table" role="table">
            <div className="quote-row head" role="row">
              <span>{t('quote.item')}</span>
              <span>{t('quote.qty')}</span>
              <span>{t('quote.unitPrice')}</span>
              <span>{t('quote.line')}</span>
              <span />
            </div>
            {draft.map((line) => (
              <div
                className={[
                  'quote-row',
                  line.source === 'work_log' || line.job_card_part_id ? 'from-work-log' : '',
                  line.line_kind === 'labor' ? 'is-labor' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="row"
                key={line.key}
              >
                <div className="quote-item-cell">
                  <input
                    aria-label={t('quote.item')}
                    placeholder={t('quote.itemPlaceholder')}
                    value={line.description}
                    onChange={(e) => setLine(line.key, { description: e.target.value, source: 'custom' })}
                  />
                  {line.source === 'work_log' || line.job_card_part_id ? (
                    <span className="quote-line-tag">{t('quote.workLog')}</span>
                  ) : null}
                  {line.line_kind === 'labor' ? (
                    <span className="quote-line-tag labor">{t('quote.labour')}</span>
                  ) : null}
                  {line.source === 'inspection' ? (
                    <span className="quote-line-tag inspection">{t('quote.inspection')}</span>
                  ) : null}
                </div>
                <div className="qty">
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    disabled={qtyOf(line.quantity) <= 1}
                    onClick={() => setLine(line.key, { quantity: qtyOf(line.quantity) - 1 })}
                  >
                    −
                  </button>
                  <input
                    aria-label="Quantity"
                    inputMode="numeric"
                    value={line.quantity}
                    onChange={(e) => setLine(line.key, { quantity: e.target.value })}
                    onBlur={() => setLine(line.key, { quantity: qtyOf(line.quantity) })}
                  />
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => setLine(line.key, { quantity: qtyOf(line.quantity) + 1 })}
                  >
                    +
                  </button>
                </div>
                <label className="unit">
                  <span>EGP</span>
                  <input
                    aria-label="Unit price"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={line.unit_price}
                    onChange={(e) => setLine(line.key, { unit_price: e.target.value })}
                  />
                </label>
                <strong className="line-sum">{money(lineTotal(line), 2)}</strong>
                <button
                  type="button"
                  className="icon-kill"
                  aria-label="Remove line"
                  onClick={() =>
                    setDraft((current) => current.filter((item) => item.key !== line.key))
                  }
                >
                  <span className="material-symbol">close</span>
                </button>
              </div>
            ))}
          </div>
          <div className="quote-toolbar">
            <button
              type="button"
              className="ghost"
              onClick={() => setDraft((current) => [...current, emptyLine()])}
            >
              {t('quote.addLine')}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() =>
                setDraft((current) => [
                  ...current,
                  {
                    ...emptyLine(),
                    line_kind: 'labor',
                    description: t('quote.labour'),
                    source: 'custom',
                  },
                ])
              }
            >
              {t('quote.addLabour')}
            </button>
            <div className="quote-total">
              <span>{t('quote.total')}</span>
              <strong>{money(draftTotal, 2)}</strong>
            </div>
          </div>
          <label>
            Note to customer
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('quote.notePlaceholder')}
            />
          </label>
          {confirmSend ? (
            <ConfirmBar
              message={
                quote
                  ? 'This replaces the quote on the customer’s phone. They will need to approve it again.'
                  : 'Send this quotation to the customer’s app?'
              }
              confirmLabel={sendLabel}
              busy={saving}
              onCancel={() => setConfirmSend(false)}
              onConfirm={() => send({ preventDefault() {} })}
            />
          ) : (
            <button type="submit" className="primary dispatch-send" disabled={saving}>
              {sendLabel}
            </button>
          )}
        </form>
      ) : null}
    </article>
  );
}

function QuoteCard({ quote, onEdit, onExport, onShare, onShareRefresh, busy }) {
  const total = (quote.items || []).reduce((sum, item) => sum + Number(item.price || 0), 0);
  const approved = (quote.items || [])
    .filter((item) => item.decision === 'approved')
    .reduce((sum, item) => sum + Number(item.price || 0), 0);

  return (
    <li>
      <header>
        <em className={`pill status-${quote.status}`}>{statusCopy(quote.status)}</em>
        <div className="quote-card-tools">
          {onExport ? (
            <button type="button" className="ghost" onClick={onExport} disabled={busy}>
              Export PDF
            </button>
          ) : null}
          {onShare ? (
            <button type="button" className="ghost" onClick={onShare} disabled={busy}>
              Share PDF
            </button>
          ) : null}
          {onEdit ? (
            <button type="button" className="ghost" onClick={onEdit}>
              Edit
            </button>
          ) : null}
          <strong>{money(total, 2)}</strong>
        </div>
      </header>
      {quote.note ? <p className="quote-note">{quote.note}</p> : null}
      <ul>
        {(quote.items || []).map((item) => (
          <li key={item.id}>
            <div>
              <strong>{item.description}</strong>
              <span>
                {item.quantity} × {money(item.unit_price, 2)}
              </span>
            </div>
            {quote.status === 'sent' ? null : (
              <span className={`decision decision-${item.decision}`}>
                {item.decision}
              </span>
            )}
            <b>{money(item.price, 2)}</b>
            {item.reject_note ? <small>{item.reject_note}</small> : null}
          </li>
        ))}
      </ul>
      {quote.status !== 'sent' ? (
        <footer>
          Approved {money(approved, 2)}
          {quote.customer_note ? ` · ${quote.customer_note}` : ''}
        </footer>
      ) : (
        <footer>Waiting on the customer.</footer>
      )}
      <QuoteShareActions quote={quote} onRefresh={onShareRefresh} />
    </li>
  );
}

function statusCopy(status) {
  return (
    {
      sent: 'Awaiting reply',
      approved: 'Approved',
      rejected: 'Rejected',
      partial: 'Partial',
      draft: 'Draft',
    }[status] || status
  );
}
